import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/templates/AuthLayout';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import { authAPI } from '@/services/api/auth.api';
import {
  createResetPasswordSchema,
  type ResetPasswordFormData,
} from '@/utils/validation/auth.schemas';
import { ROUTES } from '@/constants/routes';
import styles from './ResetPasswordPage.module.scss';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [isSuccess, setIsSuccess] = useState(false);

  const resetPasswordSchema = useMemo(() => createResetPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      await authAPI.resetPassword({ token, newPassword: data.password });
      setIsSuccess(true);
    } catch (error) {
      // El backend devuelve el motivo (enlace caducado/no válido, contraseña débil).
      setError('root', {
        message: error instanceof Error ? error.message : t('auth.reset.error'),
      });
    }
  };

  // Enlace sin token → no se puede continuar; ofrecemos pedir uno nuevo.
  if (!token) {
    return (
      <AuthLayout title={t('auth.reset.invalidTitle')} subtitle={t('auth.reset.invalidSubtitle')}>
        <div className={styles.resetPassword__success}>
          <Button asChild variant="primary" size="lg" fullWidth>
            <Link to={ROUTES.AUTH.FORGOT_PASSWORD}>{t('auth.reset.requestNew')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (isSuccess) {
    return (
      <AuthLayout
        title={t('auth.reset.titleSuccess')}
        subtitle={t('auth.reset.subtitleSuccess')}
      >
        <div className={styles.resetPassword__success}>
          <p className={styles.resetPassword__success__message}>
            {t('auth.reset.successMessage')}
          </p>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => navigate(ROUTES.AUTH.LOGIN)}
          >
            {t('auth.reset.backToLogin')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.reset.title')} subtitle={t('auth.reset.subtitle')}>
      <form className={styles.resetPassword__form} onSubmit={handleSubmit(onSubmit)}>
        {errors.root && (
          <div className={styles.resetPassword__error} role="alert">
            {errors.root.message}
          </div>
        )}

        <FormField
          label={t('auth.reset.passwordLabel')}
          type="password"
          placeholder={t('auth.reset.passwordPlaceholder')}
          error={errors.password?.message}
          helperText={t('auth.reset.passwordHelper')}
          required
          autoComplete="new-password"
          {...register('password')}
        />

        <FormField
          label={t('auth.reset.confirmPasswordLabel')}
          type="password"
          placeholder={t('auth.reset.confirmPasswordPlaceholder')}
          error={errors.confirmPassword?.message}
          required
          autoComplete="new-password"
          {...register('confirmPassword')}
        />

        <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isSubmitting}>
          {t('auth.reset.submit')}
        </Button>

        <Button asChild variant="ghost" size="md" fullWidth>
          <Link to={ROUTES.AUTH.LOGIN} className={styles.resetPassword__back}>
            {t('auth.reset.backToLogin')}
          </Link>
        </Button>
      </form>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
